import { useCallback, useState, useEffect, useRef } from 'react';
import { jwtDecode } from 'jwt-decode';
import Cookies from 'universal-cookie';
import type { AccessTokenPayload } from '@/api/auth/interfaces';
import { ACCESS_TOKEN_COOKIE } from '@/utils/authCookies';


/**
 * Low-level auth state hook. Reads the `accessToken` cookie, decodes the JWT,
 * and keeps the result in React state.
 *
 * This is the **only** place that owns auth state. Do not call it from
 * components or API hooks — use `useAuth()` instead, which reads from
 * {@link AuthProvider} context and shares a single instance app-wide.
 *
 * Responsibilities:
 * - Load and decode the token on mount
 * - Re-load when a `auth:changed` window event fires (login, logout, tenant switch, token refresh)
 * - Clear expired or invalid tokens
 * - Periodically re-check token expiry (every 5 minutes)
 *
 * Background reload: after the first load completes, subsequent `auth:changed`
 * events refresh payload without setting `loading` to true, so the router is
 * not unmounted during tenant switches or silent token refresh.
 */
export function useAuthState() {
  const [payload, setPayload] = useState<AccessTokenPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const hasLoadedOnceRef = useRef(false);
  const cookiesRef = useRef(new Cookies());

  const getCookie = useCallback((name: string): string | null => {
    const value = cookiesRef.current.get(name);
    return typeof value === 'string' && value.length > 0 ? value : null;
  }, []);

  const deleteCookie = useCallback((name: string) => {
    cookiesRef.current.remove(name, { path: '/' });
  }, []);

  const isTokenExpired = useCallback((token: string): boolean => {
    try {
      const decoded = jwtDecode<AccessTokenPayload>(token);
      if (!decoded.exp) return true;

      const currentTime = Math.floor(Date.now() / 1000);
      return decoded.exp < currentTime;
    } catch {
      return true;
    }
  }, []);

  const loadPayload = useCallback(() => {
    if (!hasLoadedOnceRef.current) {
      setLoading(true);
    }

    const token = getCookie(ACCESS_TOKEN_COOKIE);

    if (!token) {
      setPayload(null);
      setLoading(false);
      hasLoadedOnceRef.current = true;
      return;
    }

    if (isTokenExpired(token)) {
      console.warn('Token is expired, removing access token cookie');
      deleteCookie(ACCESS_TOKEN_COOKIE);
      setPayload(null);
      setLoading(false);
      hasLoadedOnceRef.current = true;
      return;
    }

    try {
      const decoded = jwtDecode<AccessTokenPayload>(token);
      setPayload(decoded);
      console.log('[LoginFlow] useAuth loadPayload: setPayload(decoded), role=', decoded?.role);
    } catch (e) {
      console.error('Failed to decode token', e);
      deleteCookie(ACCESS_TOKEN_COOKIE);
      setPayload(null);
    } finally {
      setLoading(false);
      hasLoadedOnceRef.current = true;
    }
  }, [isTokenExpired, getCookie, deleteCookie]);

  const clearAuth = useCallback(() => {
    deleteCookie(ACCESS_TOKEN_COOKIE);
    setPayload(null);
    window.location.reload();
  }, [deleteCookie]);

  useEffect(() => {
    loadPayload();
  }, [loadPayload]);

  useEffect(() => {
    const onAuthChanged = () => {
      console.log('[LoginFlow] useAuth: auth:changed received, calling loadPayload');
      loadPayload();
    };
    window.addEventListener('auth:changed', onAuthChanged);
    return () => window.removeEventListener('auth:changed', onAuthChanged);
  }, [loadPayload]);

  useEffect(() => {
    const interval = setInterval(() => {
      const token = getCookie(ACCESS_TOKEN_COOKIE);
      if (token && isTokenExpired(token)) {
        console.warn('Token expired during periodic check');
        clearAuth();
      }
    }, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [isTokenExpired, clearAuth, getCookie]);

  return {
    /** Decoded JWT payload, or null when logged out. */
    payload,
    /** True while the token is being read or decoded on initial load. */
    loading,
    /** Re-read the cookie and refresh payload (e.g. after login). */
    reload: loadPayload,
    /** Remove the token and reload the page. */
    clearAuth,
    /** True when a valid, non-expired token is present. */
    isAuthenticated: !!payload && !loading
  };
}
